import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, useTheme } from '@mui/material';
import { MarketButton } from '@market/react';
import { getDomain } from 'utilities/environment';
import { useOrderDetails } from 'components/context/order_details';
import { CheckoutFlow } from 'components/context/checkout_flow';
import Main from 'components/pages/checkout/main';
import OrderSummary from 'components/pages/checkout/order_summary';
import NavLinks from 'components/common/nav_links';
import PageLoader from 'components/common/page_loader';
import StatusBar from 'components/common/status_bar';
import Toast from 'components/common/toast';
import AddressClient from 'clients/address';
import CheckoutClient from 'clients/checkout';
import { DASHBOARD_SEARCH_PARAMS } from 'constants/enums';
import { useFeatureFlags } from 'components/context/feature_flags';
import {
  isOrderFromOnboarding,
  isDashboardMode,
  isSourceShopHardwareFlow,
} from 'helpers/search_parameter_helpers';
import CustomQueryClientProvider from 'typescript/providers/custom_query_client_provider';

const stepConfig = (warrantyCheckout) =>
  warrantyCheckout
    ? {
        steps: {
          products: [],
          informationAlt: ['/information', '/payment', '/review'],
          printLabels: [],
        },
        disableList: ['/complete'],
        labelRedirect: { products: '../../return/warranty' },
      }
    : {
        steps: {
          information: ['/information'],
          shipping: ['/shipping_method'],
          payment: [
            '/payment_option',
            '/offer_select',
            '/finance_app',
            '/payment',
          ],
          review: ['/review'],
        },
        disableList: ['/complete'],
        labelRedirect: {},
      };

const Checkout = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { number, store, language } = useOrderDetails();
  const { mpoPhase3Enabled } = useFeatureFlags();

  const [toast, setToast] = useState({});
  const [orderData, setOrderData] = useState({});

  const addressClient = useMemo(() => new AddressClient(number, store), []);

  const checkoutClient = useMemo(
    () => new CheckoutClient(number, store, language),
    [],
  );

  const isMpo = mpoPhase3Enabled && isOrderFromOnboarding();
  const isFromShopHardwareFlow = isSourceShopHardwareFlow();
  const hasSoftware = orderData?.cart_summary?.some(
    ({ is_software }) => is_software,
  );

  const orderRefresh = () => {
    return checkoutClient.orderSummary().then((data) => {
      setOrderData(data);
      return data;
    });
  };

  useEffect(() => {
    orderRefresh();
  }, []);

  const checkoutFlowValues = {
    addressClient,
    checkoutClient,
    orderData,
    orderRefresh,
    setToast,
  };

  // wait for first orderData API call to return before beginning checkout
  if (Object.keys(orderData).length === 0) {
    return <PageLoader delayMs={500} />;
  }

  const showContinueToDashboardButtonOnCompletion =
    !orderData.is_warranty &&
    window.location.pathname.includes(
      stepConfig(orderData.is_warranty)?.disableList[0], // '/complete'
    ) &&
    (isMpo || hasSoftware || isFromShopHardwareFlow);

  const buildDashboardLink = () => {
    return `https://${getDomain()}/dashboard/${
      isFromShopHardwareFlow
        ? `?${DASHBOARD_SEARCH_PARAMS.COMPLETED_SETUP_TASK.KEY}=${DASHBOARD_SEARCH_PARAMS.COMPLETED_SETUP_TASK.VALUES.BUY_HARDWARE}`
        : ''
    }`;
  };

  const handleToastClose = () => {
    setToast({ open: false });
  };

  return (
    <>
      <CustomQueryClientProvider>
        <Toast {...toast} onClose={handleToastClose} />
        <CheckoutFlow {...checkoutFlowValues}>
          {!isDashboardMode() && (
            <StatusBar stepConfig={stepConfig(orderData.is_warranty)} />
          )}
          <Grid
            container
            columnSpacing={6}
            rowSpacing={2}
            sx={{ py: 4, px: { xs: '5%', lg: '15%' } }}
          >
            <Grid item xs={12} md={8}>
              <Main />
            </Grid>
            <Grid item xs={12} md={4}>
              {showContinueToDashboardButtonOnCompletion && (
                <MarketButton
                  rank="primary"
                  href={buildDashboardLink()}
                  style={{ width: '100%', marginBottom: theme.spacing(3) }}
                >
                  {t('checkout.label.continueToDashboard')}
                </MarketButton>
              )}
              <OrderSummary />
            </Grid>
          </Grid>
        </CheckoutFlow>
        <NavLinks />
      </CustomQueryClientProvider>
    </>
  );
};

export default Checkout;
